import { SxProps, Theme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { formatDate, getTimeAgo } from 'shared/dates';
import { GeoEventMetadataText } from '../GeoEventMetadataRows/GeoEventMetadataText';
import type { GeoEventUpdateTimeInput } from './GeoEventUpdateTime.types';

type GeoEventUpdateTimeProps = {
  geoEvent: GeoEventUpdateTimeInput;
  /** Search rows — fixed size + truncate; detail headers scale with body2. */
  dense?: boolean;
  sx?: SxProps<Theme>;
};

export const GeoEventUpdateTime = (
  props: GeoEventUpdateTimeProps,
): JSX.Element | null => {
  const { geoEvent, dense = false, sx } = props;
  const { t } = useTranslation();
  const { isActive, dateModified } = geoEvent;

  if (!isActive) {
    return (
      <GeoEventMetadataText
        dense={dense}
        fontWeight="fontWeightBold"
        data-testid="geo-event-update-time"
        sx={{ display: 'block', ...sx }}
      >
        {t('geoEvent.inactive')}
      </GeoEventMetadataText>
    );
  }

  if (geoEvent.data?.geoEventType === 'wildfire') {
    const prescribedDateStart = geoEvent.data.prescribedDateStart
      ? new Date(geoEvent.data.prescribedDateStart)
      : null;

    const scheduledStart = prescribedDateStart
      ? formatDate(prescribedDateStart, 'MMM d')
      : null;

    if (scheduledStart) {
      return (
        <GeoEventMetadataText
          dense={dense}
          data-testid="geoevent-scheduled-start"
          sx={{ display: 'block', ...sx }}
        >
          <Trans
            i18nKey="geoEvent.updateTime.scheduledStart"
            values={{ scheduledStart }}
          >
            <b>Scheduled Start</b> • Apr 8
          </Trans>
        </GeoEventMetadataText>
      );
    }
  }

  const lastUpdatedContent = (
    <Trans
      i18nKey="geoEvent.updateTime.lastUpdated"
      values={{
        status: t('geoEvent.active'),
        timePass: getTimeAgo(dateModified),
      }}
    >
      <b>Active</b> • Updated <b>2 hours ago</b>
    </Trans>
  );

  return (
    <GeoEventMetadataText
      dense={dense}
      data-testid="geo-event-update-time"
      sx={{ display: 'block', ...sx }}
    >
      {lastUpdatedContent}
    </GeoEventMetadataText>
  );
};
